// Profile.js
import React, { useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { Card, CardContent, Typography, Grid, Divider, Box } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import UpdateIcon from '@mui/icons-material/Update';
import EventIcon from '@mui/icons-material/Event';
import './Styles/Pages/Profile.css';

const Profile = () => {
    const { authState, oktaAuth } = useOktaAuth();
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const getUserInfo = async () => {
            if (!authState || !authState.isAuthenticated) {
                setLoading(false);
                return;
            }

            try {
                const info = await oktaAuth.getUser();
                // Format lastLoginTime if available
                if (info.last_login && !isNaN(new Date(info.last_login).getTime())) {
                    info.formattedLastLogin = new Date(info.last_login).toLocaleString();
                } else {
                    info.formattedLastLogin = 'Not available';
                }

                // Format profile updated date
                info.formattedUpdated = new Date().toLocaleString();

                setUserInfo(info);
            } catch (error) {
                console.error('Error fetching user info:', error);
            } finally {
                setLoading(false);
            }
        };

        getUserInfo();
    }, [authState, oktaAuth]);

    if (loading) {
        return <div className="profile-loading">Loading user information...</div>;
    }

    if (!authState || !authState.isAuthenticated) {
        return <div className="profile-error">You must be logged in to view this page</div>;
    }

    return (
        <div className="profile-container">
            <Card className="profile-card">
                <CardContent>
                    <Typography variant="h4" component="h1" gutterBottom className="profile-title">
                        User Profile
                    </Typography>

                    <Divider className="profile-divider" />

                    <Box className="profile-section">
                        <Typography variant="h5" component="h2" gutterBottom className="section-title">
                            Personal Information
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <div className="info-item">
                                    <PersonIcon className="info-icon" />
                                    <div>
                                        <Typography variant="subtitle2">Name</Typography>
                                        <Typography variant="body1">{userInfo?.name || 'Not available'}</Typography>
                                    </div>
                                </div>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <div className="info-item">
                                    <EmailIcon className="info-icon" />
                                    <div>
                                        <Typography variant="subtitle2">Email</Typography>
                                        <Typography variant="body1">{userInfo?.email || 'Not available'}</Typography>
                                    </div>
                                </div>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <div className="info-item">
                                    <PersonIcon className="info-icon" />
                                    <div>
                                        <Typography variant="subtitle2">Username</Typography>
                                        <Typography variant="body1">{userInfo?.preferred_username || userInfo?.email || 'Not available'}</Typography>
                                    </div>
                                </div>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <div className="info-item">
                                    <PublicIcon className="info-icon" />
                                    <div>
                                        <Typography variant="subtitle2">Locale</Typography>
                                        <Typography variant="body1">{userInfo?.locale || 'US'}</Typography>
                                    </div>
                                </div>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider className="profile-divider" />

                    <Box className="profile-section">
                        <Typography variant="h5" component="h2" gutterBottom className="section-title">
                            Authentication Details
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <div className="info-item">
                                    <LockIcon className="info-icon" />
                                    <div>
                                        <Typography variant="subtitle2">Last Login</Typography>
                                        <Typography variant="body1">{userInfo?.formattedLastLogin}</Typography>
                                    </div>
                                </div>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <div className="info-item">
                                    <UpdateIcon className="info-icon" />
                                    <div>
                                        <Typography variant="subtitle2">Profile Updated</Typography>
                                        <Typography variant="body1">{userInfo?.formattedUpdated}</Typography>
                                    </div>
                                </div>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <div className="info-item">
                                    <EventIcon className="info-icon" />
                                    <div>
                                        <Typography variant="subtitle2">Account Created</Typography>
                                        <Typography variant="body1">
                                            {userInfo?.created && !isNaN(new Date(userInfo.created).getTime())
                                                ? new Date(userInfo.created).toLocaleString()
                                                : 'Not available'}
                                        </Typography>
                                    </div>
                                </div>
                            </Grid>
                        </Grid>
                    </Box>
                </CardContent>
            </Card>
        </div>
    );
};

export default Profile;